import { useSnackbar } from "notistack";
import { useState, useEffect, useCallback } from "react";

import { Event } from "../../../dtos/event";
import { AjaxResult } from "../../../enums/ajaxResult";
import { LogFilterRequest } from "../../../rest/requestObjects/eventLogRequest";
import { useApi } from "../../useApi";

export function useAlarms(
    dealerId: number | undefined,
    orgId: number | undefined,
    siteId: number | undefined,
    request: LogFilterRequest,
): {
    numberOfPages: number;
    isLoading: boolean;
    events: Event[];
    loadAlarms: () => void;
} {
    const { enqueueSnackbar } = useSnackbar();
    const { eventApi } = useApi();
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadAlarms = useCallback(() => {
        const abortController = new AbortController();
        let apiMethod = () => eventApi.getAlarms(request, abortController);
        if (dealerId && !orgId && !siteId) {
            apiMethod = () =>
                eventApi.getAlarmsForDealer(dealerId, request, abortController);
        }
        if (orgId && !siteId) {
            apiMethod = () => eventApi.getAlarmsForOrg(orgId, request, abortController);
        }
        if (siteId && orgId) {
            apiMethod = () =>
                eventApi.getAlarmsForSite(orgId, siteId, request, abortController);
        }
        setIsLoading(true);
        apiMethod().then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setEvents(r.data.events);
                setNumberOfPages(r.data.numberOfPages);
                setIsLoading(false);
            } else if (r.result !== AjaxResult.Cancelled) {
                enqueueSnackbar("Couldnt get events!");
                setIsLoading(false);
            }
        });
        return () => abortController.abort();
    }, [request, dealerId, orgId, siteId, eventApi, enqueueSnackbar]);

    useEffect(() => {
        return loadAlarms();
    }, [loadAlarms]);

    return {
        numberOfPages,
        isLoading,
        events,
        loadAlarms,
    };
};
